import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Mail, MapPin } from "lucide-react";
import { NAV_LINKS, SITE } from "../lib/site";
import { useEnquiry } from "./EnquiryModal";

const Brand = ({ light }) => (
  <Link to="/" data-testid="nav-brand" className="flex items-center gap-3">
    <img src="/logo.svg" alt="Paramount Kitchen logo" className="h-10 w-10" />
    <span className={`leading-none ${light ? "text-ivory" : "text-ink"}`}>
      <span className="font-display text-xl font-semibold tracking-wide">PARAMOUNT</span>
      <span className="mt-1 block text-[9px] font-semibold uppercase tracking-[0.42em] text-gold">
        Kitchen
      </span>
    </span>
  </Link>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { openEnquiry } = useEnquiry();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const light = !scrolled && !open;

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed inset-x-0 top-0 z-[80] transition-all duration-500 ${
          scrolled
            ? "border-b border-ink/10 bg-ivory/90 text-ink shadow-sm backdrop-blur-xl"
            : "border-b border-transparent text-ivory"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6">
          <Brand light={light} />

          <nav className="hidden items-center gap-7 xl:flex">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `nav-link whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.12em] transition-colors ${
                    isActive ? "is-active text-gold" : "hover:text-gold"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-book-consultation"
              onClick={() => openEnquiry({ source: "consultation" })}
              className={`hidden text-[11px] font-semibold uppercase tracking-[0.16em] underline-offset-4 hover:underline 2xl:inline 2xl:mr-2 ${
                light ? "text-ivory/85" : "text-ink/70"
              }`}
            >
              Book a Consultation
            </button>
            <button
              data-testid="nav-design-my-kitchen"
              onClick={() => openEnquiry({ source: "nav_cta" })}
              className="hidden rounded-full bg-gold px-6 py-3 text-xs font-bold uppercase tracking-[0.14em] text-royal shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:bg-golddim hover:text-ivory sm:inline-flex whitespace-nowrap"
            >
              Design My Kitchen
            </button>
            <button
              data-testid="nav-hamburger"
              onClick={() => setOpen((o) => !o)}
              aria-label={open ? "Close menu" : "Open menu"}
              className={`grid h-11 w-11 place-items-center rounded-full border transition-colors xl:hidden ${
                light || open ? "border-white/25 text-ivory" : "border-ink/15 text-ink"
              } ${open ? "bg-white/10" : ""}`}
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[75] flex flex-col bg-royal text-ivory"
          >
            <div className="pointer-events-none absolute -right-20 top-1/3 h-80 w-80 rounded-full bg-gold/10 blur-3xl" />
            <nav className="relative mt-28 flex flex-1 flex-col gap-1 px-8">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.05, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={({ isActive }) =>
                      `block border-b border-white/10 py-3 font-display text-3xl font-medium transition-colors ${
                        isActive ? "text-gold" : "text-ivory hover:text-gold"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="relative space-y-4 px-8 pb-10"
            >
              <button
                data-testid="mobile-menu-design-button"
                onClick={() => {
                  setOpen(false);
                  openEnquiry({ source: "mobile_menu" });
                }}
                className="w-full rounded-full bg-gold py-4 text-sm font-bold uppercase tracking-[0.14em] text-royal"
              >
                Design My Kitchen
              </button>
              <div className="flex flex-col gap-2 pt-2 text-sm text-ivory/70">
                <a href={`tel:${SITE.phoneHref.replace("tel:", "")}`} className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-gold" /> {SITE.phoneDisplay}
                </a>
                <a href={`mailto:${SITE.email}`} className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-gold" /> {SITE.email}
                </a>
                <span className="flex items-center gap-3">
                  <MapPin className="h-4 w-4 text-gold" /> {SITE.location}
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
